import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useCurrentUser } from '@/hooks/use-auth';
import { useRealtimeProjects } from '@/hooks/use-realtime';
import { useI18n } from '@/lib/i18n';
import { getSafeRedirectPath } from '@/lib/auth-redirect';
import { AUTH_EXPIRED_EVENT } from '@/lib/api';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const location = useLocation();
    const { isAuthenticated, isLoading, user, logout } = useAuthStore();
    const { isLoading: isQueryLoading } = useCurrentUser();
    const { initializeLanguage } = useI18n();

    // Subscribe to realtime project updates (including deletions) for all authenticated pages
    useRealtimeProjects();

    // Initialize language from user preferences when user is loaded
    useEffect(() => {
        if (user && (user as any).language) {
            initializeLanguage((user as any).language);
        }
    }, [user, initializeLanguage]);

    useEffect(() => {
        const handleExpiredSession = () => logout();
        window.addEventListener(AUTH_EXPIRED_EVENT, handleExpiredSession);
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpiredSession);
    }, [logout]);

    // Show loading state while checking authentication
    if (isLoading || isQueryLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    <p className="text-foreground-secondary">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
}

interface GuestOnlyRouteProps {
    children: React.ReactNode;
}

export function GuestOnlyRoute({ children }: GuestOnlyRouteProps) {
    const location = useLocation();
    const { isAuthenticated, isLoading } = useAuthStore();
    const { isLoading: isQueryLoading } = useCurrentUser();

    // Show loading state while checking authentication
    if (isLoading || isQueryLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    <p className="text-foreground-secondary">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to dashboard if already authenticated
    if (isAuthenticated) {
        const queryRedirect = new URLSearchParams(location.search).get('redirect');
        const stateRedirect = (location.state as { from?: Location })?.from?.pathname;
        const from = getSafeRedirectPath(queryRedirect || stateRedirect);
        return <Navigate to={from} replace />;
    }

    return <>{children}</>;
}
